import { IcoBrand } from "assets/icons";
import { Navbar } from "components";
import CaseSection from "components/CaseSection";
import WorksCarousel from "components/WorksSection/WorksCarousel";
import { useWorkCases } from "hooks";
import { Helmet } from "react-helmet-async";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import {
	buildCanonicalUrl,
	buildCaseDescription,
	buildCaseTitle,
	buildOgImageAlt,
	getCaseOgImage,
	homeDescription,
	homeTitle,
	keywords,
	siteName,
	siteUrl
} from "utils/seo";
import NotFound from "./NotFound";
import "./styles.scss";

function CasePage() {
	const { slug } = useParams();
	const cases = useWorkCases();
	const { t } = useTranslation();

	const caseData = cases.find((c) => c.slug === slug);
	const caseTitle = caseData?.title;
	const pageTitle = caseTitle ? buildCaseTitle(caseTitle) : homeTitle;
	const pageDescription = caseData
		? buildCaseDescription(caseData.title)
		: homeDescription;
	const canonicalUrl =
		caseData && slug
			? buildCanonicalUrl(`/case/${slug}`)
			: buildCanonicalUrl("/");
	const ogImage = getCaseOgImage(slug);
	const ogImageAlt = buildOgImageAlt(caseTitle);
	const ogType = caseData ? "article" : "website";
	const robotsContent = caseData ? "index,follow" : "noindex,follow";
	const keywordsContent = caseTitle ? `${keywords}, ${caseTitle}` : keywords;
	const caseJsonLd = caseData
		? {
				"@context": "https://schema.org",
				"@type": "CreativeWork",
				name: caseTitle,
				url: canonicalUrl,
				description: pageDescription,
				image: ogImage,
				inLanguage: "pt-BR",
				author: {
					"@type": "Person",
					name: siteName,
					url: siteUrl
				},
				isPartOf: {
					"@type": "WebSite",
					name: siteName,
					url: siteUrl
				},
				keywords: keywordsContent
		  }
		: null;

	return (
		<div className="case-page">
			<Helmet>
				<title>{pageTitle}</title>
				<meta name="description" content={pageDescription} />
				<meta name="keywords" content={keywordsContent} />
				<meta name="robots" content={robotsContent} />
				<meta
					name="googlebot"
					content={
						caseData
							? "index,follow,max-image-preview:large,max-snippet:-1,max-video-preview:-1"
							: robotsContent
					}
				/>
				<link rel="canonical" href={canonicalUrl} />
				<meta property="og:type" content={ogType} />
				<meta property="og:site_name" content={siteName} />
				<meta property="og:locale" content="pt_BR" />
				<meta property="og:title" content={pageTitle} />
				<meta property="og:description" content={pageDescription} />
				<meta property="og:url" content={canonicalUrl} />
				<meta property="og:image" content={ogImage} />
				<meta property="og:image:alt" content={ogImageAlt} />
				<meta name="twitter:card" content="summary_large_image" />
				<meta name="twitter:title" content={pageTitle} />
				<meta name="twitter:description" content={pageDescription} />
				<meta name="twitter:image" content={ogImage} />
				{caseJsonLd && (
					<script type="application/ld+json">
						{JSON.stringify(caseJsonLd)}
					</script>
				)}
			</Helmet>
			<Navbar mainPage={false} />
			{caseData ? <CaseSection {...caseData} /> : <NotFound />}
			<div className="other-cases">
				<div className="mb-[5%]">
					<IcoBrand theme="purple" size="35" />
				</div>
				<hr className="other-works-division-line" />
				<div className="other-cases-title">{t("other_works")}</div>
				<WorksCarousel />
			</div>
		</div>
	);
}

export default CasePage;
